import type { UserFilterKeyValue, TargetValue } from '@/api-client/dashboard/model';
import { convertCamelToCapitalStart } from '@/common/utils/core';
import { SERVER_TAG_SEPARATOR } from '@/common/define/common.define';
import { convertGlobalFilterValueToTargetTagData } from '@/dashboard/features/globalVariables/services/adaptor';
import type {
  GlobalVariable,
  SelectedGlobalVariable,
  SelectedTreeVariable,
  TargetTagData,
} from './globalVariable.types';
import type { TreeVarFilterDataForView } from '../components/globalVarFilter/types/globalVarFilter.type';
import type { ExternalVariablesOptions } from '../components/externalVariableWindow/types/externalVariableWindow.type';

/**
 * TargetTagData가 TargetValue와 일치하는지 확인하는 predicate
 */
const isMatchingTargetValue = (target: TargetTagData, value: TargetValue): boolean =>
  value.tagKey === target.groupTagId &&
  value.tagValueId === target.tagId &&
  value.tagType === target.tagType;

/**
 * 단일 선택 변수의 초기 selectedList를 계산합니다.
 * 다중 선택이거나 선택 가능한 타겟이 없으면 빈 배열을 반환합니다.
 */
const getSingleSelectDefaultList = (variable: GlobalVariable): TargetTagData[] => {
  if (variable.isMultiSelectType) return [];
  const firstTarget = variable.allTargets?.find(({ tagId }) => tagId !== '*');
  return firstTarget ? [firstTarget] : [];
};

/**
 * GlobalVariable과 프리셋 필터 정보를 결합하여 SelectedGlobalVariable을 생성합니다.
 *
 * @param variable - 글로벌 변수 정보
 * @param filter - 프리셋에 저장된 필터 정보 (없으면 단일 선택 변수는 첫 타겟, 다중 선택 변수는 빈 selectedList로 생성)
 * @returns 프리셋에서 사용할 SelectedGlobalVariable
 */
export const createGlobalVariableFilter = (
  variable: GlobalVariable,
  filter?: UserFilterKeyValue,
): SelectedGlobalVariable => {
  if (!filter) {
    return {
      globalFilterId: variable.globalFilterId,
      aliasName: variable.aliasName,
      category: variable.tagKey,
      selectedList: getSingleSelectDefaultList(variable),
      use: variable.use,
    };
  }

  const targetIdSet = new Set(
    variable.allTargets?.map(
      ({ groupTagId, tagType, tagId }) => `${groupTagId}--${tagType}--${tagId}`,
    ) ?? [],
  );

  const matchedSelectedList =
    filter.targets
      ?.filter(({ tagKey, tagType, tagValueId }) =>
        targetIdSet.has(`${tagKey}--${tagType}--${tagValueId}`),
      )
      ?.map((target) =>
        convertGlobalFilterValueToTargetTagData({
          ...target,
          tagName:
            target.tagName ??
            variable.allTargets?.find((t) => isMatchingTargetValue(t, target))?.tagName ??
            '',
        }),
      ) ?? [];

  const selectedList = matchedSelectedList.length
    ? matchedSelectedList
    : getSingleSelectDefaultList(variable);

  return {
    globalFilterId: variable.globalFilterId,
    aliasName: variable.aliasName,
    use: filter.enable ?? true,
    category: variable.tagKey,
    selectedList,
  };
};

/**
 * ExternalVariable과 프리셋 필터 정보를 결합하여 SelectedGlobalVariable을 생성합니다.
 *
 * @param variable - 외부 변수 정보 (Datadog, NewRelic 등)
 * @param filter - 프리셋에 저장된 필터 정보 (없으면 빈 selectedList로 생성)
 * @returns 프리셋에서 사용할 SelectedGlobalVariable
 */
export const createExternalVariableFilter = (
  variable: ExternalVariablesOptions,
  filter?: UserFilterKeyValue,
): SelectedGlobalVariable => {
  if (!filter) {
    return {
      globalFilterId: variable.extraGlobalFilterId!,
      aliasName: variable.name || '',
      category: variable.extraType ?? '',
      selectedList: [],
      use: true,
    };
  }

  const selectedList =
    filter.targets
      ?.filter(({ tagKey }) => tagKey != null && variable.values?.includes(tagKey))
      ?.map<TargetTagData>(({ tagKey }) => ({
        groupTagName: convertCamelToCapitalStart(tagKey?.replace(SERVER_TAG_SEPARATOR, '') ?? ''),
        tagName: tagKey ?? '',
        groupTagId: tagKey ?? '',
        tagId: tagKey ?? '',
        tagType: 'tag',
      })) ?? [];

  return {
    globalFilterId: variable.extraGlobalFilterId!,
    aliasName: variable.name || '',
    use: filter.enable ?? true,
    category: variable.extraType ?? '',
    selectedList,
  };
};

/**
 * TreeVariable과 프리셋 필터 정보를 결합하여 SelectedTreeVariable을 생성합니다.
 * 트리 변수는 objectType이 workload일 때 tagKey가 여러 개일 수 있으므로
 * category 대신 globalFilterId로 매칭합니다.
 */
export const createTreeVariableFilter = (
  variable: TreeVarFilterDataForView,
  filter?: UserFilterKeyValue,
): SelectedTreeVariable => {
  if (!filter) {
    return {
      treeVariableId: variable.id,
      use: variable.use,
      operationType: variable.operationType,
      targetFilterType: variable.targetFilterType,
      selectedList: [],
    };
  }

  return {
    treeVariableId: variable.id,
    use: filter.enable ?? true,
    operationType: variable.operationType,
    targetFilterType: variable.targetFilterType,
    selectedList:
      filter.targets?.map(({ tagKey, tagType, tagValueId }) => ({
        tagKey: tagKey ?? '',
        tagType: tagType ?? '',
        tagValueId: tagValueId ?? '',
      })) ?? [],
  };
};
